import React, { useEffect, useRef } from "react";
import { gsap, Power1, Cubic } from "gsap";
import * as createjs from "createjs-module";

// Utility functions
const range = (min: number, max: number): number => min + (max - min) * Math.random();

const round = (num: number, precision: number): number => {
  const decimal = Math.pow(10, precision);
  return Math.round(decimal * num) / decimal;
};

const weightedRange = (
  to: number,
  from = 0,
  decimalPlaces = 0,
  weightedRange: [number, number] | null = null,
  weightStrength = 0
): number => {
  if (to === from) return to;
  if (weightedRange && Math.random() <= weightStrength) {
    return round(
      Math.random() * (weightedRange[1] - weightedRange[0]) + weightedRange[0],
      decimalPlaces
    );
  } else {
    return round(Math.random() * (to - from) + from, decimalPlaces);
  }
};

const ParticleCanvas: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stageRef = useRef<createjs.Stage | null>(null);
  const particles: createjs.Shape[] = [];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const stage = new createjs.Stage(canvas);
    stageRef.current = stage;

    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    const totalWidth = canvas.width;
    const totalHeight = canvas.height;

    stage.compositeOperation = "lighter";

    // Draw particles
    const drawParticles = () => {
      const particleSettings = [
        { num: 10, fromX: 0, toX: totalWidth, radius: 20, alphaMax: 0.25, areaHeight: 5, color: "#ffff88" },
        { num: 10, fromX: 0, toX: totalWidth, radius: 40, alphaMax: 0.2, areaHeight: 5, color: "#ffeeee" },
        { num: 8, fromX: 0, toX: totalWidth, radius: 60, alphaMax: 0.2, areaHeight: 20, color: "#ddffdd" },
        { num: 5, fromX: 0, toX: totalWidth, radius: 80, alphaMax: 0.1, areaHeight: 20, color: "#ffffcc" },
      ];

      particleSettings.forEach((setting) => {
        for (let i = 0; i < setting.num; i++) {
          const circle = new createjs.Shape();
          circle.graphics.clear();
          circle.graphics.beginFill(setting.color).drawCircle(0, 0, setting.radius);

          circle.alpha = range(0, 0.1);
          const initY = weightedRange(
            0,
            totalHeight,
            1,
            [totalHeight * (2 - setting.areaHeight / 2) / 4, totalHeight * (2 + setting.areaHeight / 2) / 4],
            0.8
          );
          const initX = weightedRange(
            setting.fromX,
            setting.toX,
            1,
            [setting.fromX + (setting.toX - setting.fromX) / 4, setting.fromX + (setting.toX - setting.fromX) * 3 / 4],
            0.6
          );

          circle.x = initX;
          circle.y = initY;
          circle.scaleX = circle.scaleY = 1; // ensure perfect circle

          stage.addChild(circle);
          animateBall(circle, setting);
          particles.push(circle);
        }
      });
    };

    const animateBall = (ball: createjs.Shape, setting: any) => {
      const distance = setting.radius;
      const speed = range(5, 15);
      const targetX = range(ball.x - distance, ball.x + distance);
      const targetY = range(ball.y - distance, ball.y + distance);
      const alphaMax = setting.alphaMax;

      gsap.to(ball, {
        duration: speed,
        x: targetX,
        y: targetY,
        scaleX: 1,
        scaleY: 1,
        ease: Cubic.easeInOut,
        onComplete: () => animateBall(ball, setting),
      });

      gsap.to(ball, {
        duration: speed / 2,
        alpha: range(0.1, alphaMax),
        onComplete: () => {
          gsap.to(ball, {
            duration: speed / 2,
            alpha: 0,
          });
        },
      });
    };

    drawParticles();
    createjs.Ticker.addEventListener("tick", stage);

    return () => {
      createjs.Ticker.removeAllEventListeners();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      id="banner-animation"
      style={{ position: 'absolute', top: 0, right: 0, left: 0, bottom: 0, width: '100%', height: '100%'  }}
    />
  );
};

export default ParticleCanvas;
